import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.variantChanged.bind(this));
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */

  optionalColorSwatchClick(event) {
    const target = event.target;

    if (!(target instanceof HTMLInputElement) || target.type !== 'radio') {
      return;
    }

    const normalize = (value) =>
      value
        .toLowerCase()
        .replace(/\//g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    const selectedValue = normalize(target.value);

    const syncColorsToColor = () => {
      const name = target.getAttribute('name') || '';

      if (!name.toLowerCase().startsWith('colors-')) return;

      const colorGroupName = name.replace(/^colors-/i, 'Color-');
      const matchingColorInputs = this.querySelectorAll(`input[type="radio"][name="${colorGroupName}"]`);
      let synced = false;

      matchingColorInputs.forEach((input) => {
        const inputValue = normalize(input.value);
        if (inputValue === selectedValue && !input.checked) {
          synced = true;
          input.click();
        }
      });
    };

    const syncColorToColors = () => {
      const colorsInputs = this.querySelectorAll('input[data-option-name="colors"]');
      let synced = false;

      colorsInputs.forEach((input) => {
        const inputValue = normalize(input.value);
        if (inputValue === selectedValue && !input.checked) {
          synced = true;
          input.click();
        }
      });
    };

    // Product cards use swatches-variant-picker-component
    if (target.closest('swatches-variant-picker-component')) {
      if (target.dataset.optionName === 'colors') {
        syncColorsToColor();
      } else if (target.dataset.optionName === 'color') {
        syncColorToColors();
      }
      return;
    }

    // Product page / sticky variant picker
    if (target.dataset.optionName === 'colors') {
      syncColorsToColor();
    } else if (target.dataset.optionName === 'color') {
      syncColorToColors();
    }
  }
  variantChanged(event) {
    this.optionalColorSwatchClick(event)

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    if (event.target.dataset.optionName === 'color' && !isOnProductPage) {
      const selectedColorValue = event.target.value;

      const matchingColorInput = document.querySelector(
        `input[data-option-name="colors"][value="${selectedColorValue}"]`
      );

      if (matchingColorInput) {
        this.matchingUrl = matchingColorInput.dataset.connectedProductUrl;
      }
    }
    if (!(event.target instanceof HTMLElement)) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: event.target.dataset.optionValueId ?? '' }));

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = event.target.dataset.connectedProductUrl || this.matchingUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(event.target), loadsNewProduct);

    const url = new URL(window.location.href);

    let variantId;

    if (event.target instanceof HTMLInputElement && event.target.type === 'radio') {
      variantId = event.target.dataset.variantId || null;
    } else if (event.target instanceof HTMLSelectElement) {
      const selectedOption = event.target.options[event.target.selectedIndex];
      variantId = selectedOption?.dataset.variantId || null;
    }
    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    const isAvailable = target.dataset.optionAvailable;
    
    
    // const varIDColor = target.dataset.variantId;
    // const inputIdColor = target.dataset.inputId;


    const allStickyInputElements = document.querySelectorAll(`sticky-add-to-cart input`);
    if(allStickyInputElements.length > 0){
      allStickyInputElements.forEach((input)=>{
        input.removeAttribute("checked");
      })
    }

    // Size option select
    const varID = target.dataset.variantId;
    const inputId = target.dataset.inputId;
    const stickyInputElement = document.querySelector(`sticky-add-to-cart input[data-input-id="${inputId}"][data-variant-id="${varID}"]`);
    if(stickyInputElement){
      stickyInputElement.setAttribute("checked","");
    }

    // Color option select
    // main forms selected color element
    const mainColorElement = document.querySelector(`variant-picker input[data-option-name="colors"][type="radio"]:checked`);
    if(mainColorElement){
      const varIDColor = mainColorElement.dataset.variantId;
      const inputIdColor = mainColorElement.dataset.inputId;
      const colorSticky = document.querySelector(`sticky-add-to-cart input[data-option-name="colors"][data-input-id="${inputIdColor}"][data-variant-id="${varIDColor}"]`);
      if(colorSticky) colorSticky.setAttribute("checked","");
    }


    const btnElement = document.querySelector(".sticky-add-to-cart__submit");
    const stickyBtnElement = document.querySelector(".sticky-add-to-cart__button");

    if (btnElement && stickyBtnElement) {
      if (isAvailable === "false") {
        btnElement.innerHTML = "Out of Stock";
        btnElement.setAttribute("disabled", "disabled");
        stickyBtnElement.innerHTML = "Out of Stock";
        stickyBtnElement.setAttribute("disabled", "disabled");
      } else {
        btnElement.innerHTML = "Add to Cart";
        btnElement.removeAttribute("disabled");
        stickyBtnElement.innerHTML = "Add to Cart";
        stickyBtnElement.removeAttribute("disabled");
      }

    }

    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLSelectElement) {
      target.checked = true;
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);


      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;

        // console.log("html::",html);
        // console.log("window.scrollY::",window.scrollY);

        // if(window.scrollY > 800){
        //   const ele = document.querySelector('sticky-add-to-cart');
        //   if(ele) {
        //       const btn = ele.querySelector('.sticky-add-to-cart__button');
        //       if (btn instanceof HTMLElement) {
        //           btn.style.display = 'none';
        //       }
        //       const ele2 = document.querySelector('.sticky-add-to-cart__variants');
        //       if (ele2 instanceof HTMLElement) {
        //           ele2.style.display = 'block';
        //       }
        //   }
        // }
        // Removed individual option availability check - now using combined variant availability from VariantUpdateEvent

        
        


        if (!textContent) {
          return;
        }

        // Parse variant data from the script tag (this now contains the correct combined variant)
        let variantData = null;
        try {
          variantData = JSON.parse(textContent);
          if (variantData === null || (typeof variantData === 'string' && variantData === 'null')) {
            variantData = null;
          }
        } catch (e) {
          console.error("variant-picker: Failed to parse variant data:", e);
          variantData = null;
        }

        const newVariantPicker = html.querySelector('variant-picker');
        const newProductInfo =
          newVariantPicker instanceof HTMLElement
            ? {
                id: newVariantPicker.dataset.productId,
                url: newVariantPicker.dataset.productUrl,
              }
            : null;

        // Verify that the variant actually matches all selected options
        // This prevents using fallback variants when no valid combination exists
        // IMPORTANT: We still want to find out-of-stock variants to show "Out of Stock" message
        // NOTE: We trust the server response for variant matching, so we don't do strict client-side verification
        // The server already returns the correct variant based on selected options
        // Only verify if variant is clearly wrong (e.g., completely different product)

        if (shouldMorphMain) {
          // Check what's in response HTML before morphing
          const htmlPrice = html.querySelector('product-price [ref="priceContainer"]');
          const htmlGallery = html.querySelector('media-gallery');
          const htmlImages = htmlGallery ? htmlGallery.querySelectorAll('img') : [];
          console.log('[variant-picker] Before morph - Response HTML contains:', {
            hasPrice: !!htmlPrice,
            priceContent: htmlPrice?.innerHTML?.substring(0, 200),
            hasGallery: !!htmlGallery,
            imageCount: htmlImages.length,
            firstImageSrc: htmlImages[0]?.src,
          });

          this.updateMain(html);
          
          // When morphing main, the DOM is already fully updated with new product-price, media-gallery, etc.
          // We still need to dispatch the event for other components (like sticky-add-to-cart, product-form)
          // but we should use the current DOM after morphing, not the response HTML
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              // Check what's in DOM after morphing
              const domPrice = document.querySelector('product-price [ref="priceContainer"]');
              const domGallery = document.querySelector('media-gallery');
              const domImages = domGallery ? domGallery.querySelectorAll('img') : [];
              console.log('[variant-picker] After morph - Current DOM contains:', {
                hasPrice: !!domPrice,
                priceContent: domPrice?.innerHTML?.substring(0, 200),
                hasGallery: !!domGallery,
                imageCount: domImages.length,
                firstImageSrc: domImages[0]?.src,
              });

              const sourceId = this.selectedOptionId || this.dataset.blockId || 'variant-picker';
              
              // For components that need the HTML (like product-form), use the response HTML
              // But product-price and media-gallery are already updated by morph, so they'll find
              // the correct content in the current DOM
              this.#dispatchVariantEvent({
                variantData,
                sourceId,
                html,
                productId: newProductInfo?.id || this.dataset.productId || '',
                newProduct: newProductInfo,
              });
            });
          });
        }
        else {
          const newProduct = this.updateVariantPicker(html);

          // Always dispatch VariantUpdateEvent (even with null variant)
          // This ensures button state updates correctly when no valid variant exists
          
          const sourceId = this.selectedOptionId || this.dataset.blockId || 'variant-picker';
          this.#dispatchVariantEvent({
            variantData,
            sourceId,
            html,
            productId: this.dataset.productId ?? '',
            newProduct,
          });
        }


      })
      .catch((error) => {
        // AbortError is expected when user changes variant quickly - don't log as error
        if (error.name === 'AbortError') {
          // Silently ignore - this is expected behavior when variant changes rapidly
          return;
        } else {
          console.error('variant-picker: Fetch error:', error);
        }
      });
  }

  #dispatchVariantEvent({ variantData, sourceId, html, productId, newProduct }) {
    const event = new VariantUpdateEvent(variantData, sourceId, {
      html,
      productId,
      newProduct,
    });

    const closestSection = this.closest('.shopify-section, dialog');
    this.dispatchEvent(event);
    closestSection?.dispatchEvent(event);
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
